import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Menu,
  MenuItem,
} from '@mui/material'
import { Visibility as ViewIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon } from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import type { Card as CardType } from '@/types'

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}
function formatCardDate(dateStr: string | undefined): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleDateString('ru-RU', DATE_FORMAT_OPTIONS)
}

function getProgressColor(percent: number): 'error' | 'warning' | 'success' {
  if (percent <= 30) return 'error'
  if (percent <= 70) return 'warning'
  return 'success'
}

function getWordText(card: CardType): string {
  return card.word?.text ?? ''
}

function getWordTranscription(card: CardType): string {
  const attrs = card.word?.attributes
  if (!attrs) return ''
  const a = attrs.find((x) => x.key === 'transcription')
  return a?.value ?? ''
}

function getWordTranslation(card: CardType): string {
  const attrs = card.word?.attributes
  if (!attrs) return ''
  const a = attrs.find((x) => x.key === 'translation')
  return a?.value ?? ''
}

interface CardListItemProps {
  card: CardType
  onDelete?: (card: CardType) => void
  showActions?: boolean
  userId?: number
  orderBy?: 'created_at' | 'updated_at' | 'progress'
}

export function CardListItem({ card, onDelete, showActions = true, userId, orderBy = 'created_at' }: CardListItemProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const progress = Math.round(card.progress ?? 0)
  const color = getProgressColor(progress)
  const viewUrl = userId != null ? ROUTES.USER_CARD_VIEW(userId, card.id) : ROUTES.CARDS_VIEW(card.id)

  const handleView = () => {
    setMenuAnchor(null)
    navigate(viewUrl)
  }
  const handleDelete = () => {
    setMenuAnchor(null)
    onDelete?.(card)
  }

  const displayDate = orderBy === 'updated_at' ? card.updated_at : card.created_at

  return (
    <Card
      sx={{
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" component={Link} to={viewUrl} sx={{ fontWeight: 600, textDecoration: 'none', color: 'inherit' }}>
              {getWordText(card)}
            </Typography>
            {(getWordTranscription(card) || getWordTranslation(card)) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {[getWordTranscription(card), getWordTranslation(card)].filter(Boolean).join(' · ') || '—'}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, progress))}
                color={color}
                sx={{ flex: 1, height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption">{progress}%</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                {t('cards.clues')}: {card.clues_count ?? card.clues?.length ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('cards.date')}: {formatCardDate(displayDate)}
              </Typography>
            </Box>
          </Box>
          {showActions && (
            <>
              <IconButton
                size="small"
                aria-label={t('common.actions')}
                onClick={(e) => {
                  e.preventDefault()
                  setMenuAnchor(e.currentTarget)
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleView}>
                  <ViewIcon sx={{ mr: 1 }} fontSize="small" />
                  {t('cards.view')}
                </MenuItem>
                {onDelete && (
                  <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                    {t('cards.delete')}
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
